import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Flame, CheckCircle2, RefreshCcw, Trophy } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { getMyStreak } from "@/lib/streak.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/streaks")({
  head: () => ({ meta: [{ title: "Sequências — Viny Club" }] }),
  component: StreaksPage,
});

const MILESTONES = [
  { days: 3, label: "Aquecendo" },
  { days: 7, label: "Uma semana em chamas" },
  { days: 30, label: "Mês incendiário" },
  { days: 100, label: "Chama eterna" },
];

function formatDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });
}

function StreaksPage() {
  const fetchStreak = useServerFn(getMyStreak);
  const q = useQuery({
    queryKey: ["my-streak"],
    queryFn: () => fetchStreak(),
    staleTime: 60_000,
  });

  const current = q.data?.current ?? 0;
  const longest = q.data?.longest ?? 0;
  const total = q.data?.total ?? 0;
  const days = q.data?.days ?? [];

  // Group days into weeks of 7, oldest first
  const weeks: typeof days[] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  const recent = [...days].reverse().filter((d) => d.active).slice(0, 12);

  return (
    <AppShell>
      <PageHeader eyebrow="Conta" title="Sequência" subtitle="Mantenha o ritmo diário e acumule XP." />
      <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 lg:px-5">


        {/* Hero card */}
        <section className="mt-4 overflow-hidden rounded-3xl border border-white/10 bg-neutral-900 p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="inline-flex items-center gap-2">
              <Flame className="h-6 w-6 text-amber-400" />
              <h1 className="font-display text-2xl font-semibold tracking-tight">
                Sequências
              </h1>
            </div>
            <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs font-medium text-amber-300">
              {current > 0 ? "Sequência ativa" : "Sem atividade hoje"}
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-2">
            <span className="font-display text-7xl font-semibold leading-none tabular-nums">
              {current}
            </span>
            <span className="text-base text-neutral-400">
              {current === 1 ? "dia consecutivo" : "dias consecutivos"}
            </span>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat label="Maior sequência" value={`${longest} ${longest === 1 ? "dia" : "dias"}`} />
            <Stat label="Dias ativos (1 ano)" value={String(total)} />
            <Stat label="Aproveitamento" value={`${Math.round((total / Math.max(days.length, 1)) * 100)}%`} />
          </div>
        </section>

        {/* Calendar */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Últimos 90 dias
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Cada quadrado é um dia. Quadrados acesos representam dias com atividade.
          </p>
          <div className="mt-5 flex gap-1.5 overflow-x-auto">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-1.5">
                {week.map((d) => (
                  <div
                    key={d.date}
                    title={`${formatDate(d.date)}${d.active ? " — ativo" : ""}`}
                    className={cn(
                      "h-5 w-5 rounded-[5px] border transition-colors",
                      d.active
                        ? "border-amber-300 bg-amber-400 shadow-[0_0_6px_-1px_rgb(251_191_36_/_0.7)]"
                        : "border-neutral-300 bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800",
                    )}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-[3px] border border-neutral-300 bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800" />
              Sem atividade
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-[3px] border border-amber-300 bg-amber-400" />
              Dia ativo
            </span>
          </div>
        </section>

        {/* Milestones */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <div className="inline-flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-400" />
            <h2 className="font-display text-lg font-semibold tracking-tight">
              Conquistas de sequência
            </h2>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {MILESTONES.map((m) => {
              const earned = longest >= m.days;
              const pct = Math.min(100, (longest / m.days) * 100);
              return (
                <div
                  key={m.days}
                  className={cn(
                    "rounded-2xl border p-4 transition-colors",
                    earned
                      ? "border-amber-400/40 bg-amber-400/10"
                      : "border-[color:var(--hairline)] bg-surface/40",
                  )}
                >
                  <div
                    className={cn(
                      "grid h-10 w-10 place-items-center rounded-full",
                      earned ? "bg-amber-400/20 text-amber-300" : "bg-surface text-muted-foreground",
                    )}
                  >
                    <Flame className="h-5 w-5" />
                  </div>
                  <div className="mt-3 text-sm font-semibold">{m.label}</div>
                  <div className="text-xs text-muted-foreground">{m.days} dias</div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        earned ? "bg-amber-400" : "bg-muted-foreground/40",
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* History */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Histórico recente
          </h2>
          {recent.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Nenhuma atividade registrada ainda — comece curtindo, comentando ou
              adicionando um disco.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-[color:var(--hairline)]">
              {recent.map((d) => (
                <li key={d.date} className="flex items-center justify-between py-2.5">
                  <span className="text-sm">{formatDate(d.date)}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-400">
                    <Flame className="h-3.5 w-3.5" /> Dia ativo
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* How it works */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Como funcionam
          </h2>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
              Faça pelo menos uma ação por dia (curtir, comentar, adicionar disco).
            </li>
            <li className="flex items-start gap-3">
              <Flame className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              Cada dia consecutivo aumenta sua sequência e gera XP bônus.
            </li>
            <li className="flex items-start gap-3">
              <RefreshCcw className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              Ficar 1 dia sem atividade reinicia o contador.
            </li>
          </ul>
        </section>
      </div>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400">
        {label}
      </div>
      <div className="mt-1 font-display text-xl font-semibold tabular-nums">
        {value}
      </div>
    </div>
  );
}
